import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Paper, Divider } from '@mui/material';
import { Typography, PageHeader, StatCard } from '@/components/ui';
import { useTransactionBreadcrumbs } from '../../utils';
import { TRANSACTION_STATUSES, getStatusColor } from '../../constants';
import { transactionsData } from '../../data';
import type { Transaction } from '../../types';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Receipt as ReceiptIcon,
  AccountBalance as BankIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

// Mock transaction data
const mockTransactions: Transaction[] = transactionsData as Transaction[];

export const TransactionDetailPage: React.FC = () => {
  const { transactionId } = useParams<{ transactionId: string }>();
  const navigate = useNavigate();

  // Find the transaction
  const transaction = mockTransactions.find(t => t.transaction_id === transactionId);

  // Generate dynamic breadcrumbs for transaction details
  const breadcrumbs = useTransactionBreadcrumbs(
    transactionId,
    transaction?.merchant_transaction_id,
    transaction?.payment_gateway
  );

  if (!transaction) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6" color="textSecondary">
          Transaction not found
        </Typography>
      </Box>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(new Date(dateString));
  };

  const handleGoBack = () => {
    navigate('/transactions');
  };

  return (
    <Box>
      {/* Page Header */}
      <PageHeader
        title={`Payment ${transaction.transaction_id}`}
        subtitle={`View details for transaction ${transaction.transaction_id}`}
        breadcrumbs={breadcrumbs}
        actions={[
          {
            label: 'Back to Transactions',
            icon: <ArrowBackIcon sx={{ fontSize: 16 }} />,
            onClick: handleGoBack,
            variant: 'outlined',
            size: 'medium',
          },
          {
            label: 'Download Receipt',
            icon: <DownloadIcon sx={{ fontSize: 16 }} />,
            onClick: () => console.log('Download receipt for:', transaction.transaction_id),
            variant: 'contained',
            size: 'medium',
          },
          {
            label: 'Edit',
            icon: <EditIcon sx={{ fontSize: 16 }} />,
            onClick: () => console.log('Edit transaction:', transaction.transaction_id),
            variant: 'outlined',
            size: 'medium',
          },
        ]}
        showDivider={true}
      />

      {/* Transaction Summary Cards */}
      <Box 
        display="grid" 
        gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }} 
        gap={{ xs: 2, sm: 3 }} 
        mb={4}
      >
        <StatCard
          title="Transaction Amount"
          value={formatCurrency(transaction.transaction_amount)}
          icon={<ReceiptIcon />}
          color="primary"
          size="medium"
        />
        <StatCard
          title="Payment Gateway"
          value={transaction.payment_gateway}
          icon={<BankIcon />}
          color="info"
          size="medium"
        />
        <StatCard
          title="Status"
          value={TRANSACTION_STATUSES[transaction.transaction_status]}
          icon={<CheckCircleIcon />}
          color={getStatusColor(transaction.transaction_status) as 'success' | 'error' | 'warning' | 'info' | 'primary'}
          size="medium"
        />
        <StatCard
          title="Transaction Time"
          value={formatDate(transaction.created_at)}
          icon={<ScheduleIcon />}
          color="secondary"
          size="medium"
        />
      </Box>

      <Box 
        display="grid" 
        gridTemplateColumns={{ xs: '1fr', lg: '1fr 1fr' }} 
        gap={{ xs: 2, sm: 3 }}
      >
        {/* Transaction Details */}
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" gutterBottom fontWeight="medium">
            Transaction Details
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="textSecondary">
                Transaction ID:
              </Typography>
              <Typography variant="body2" fontWeight="medium" fontFamily="monospace">
                {transaction.transaction_id}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="textSecondary">
                Merchant Transaction ID:
              </Typography>
              <Typography variant="body2" fontWeight="medium" fontFamily="monospace">
                {transaction.merchant_transaction_id}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="textSecondary">
                Amount:
              </Typography>
              <Typography variant="body2" fontWeight="medium" sx={{ color: 'success.main' }}>
                {formatCurrency(transaction.transaction_amount)}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="textSecondary">
                Payment Gateway:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {transaction.payment_gateway}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="textSecondary">
                Status:
              </Typography>
              <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ color: `${getStatusColor(transaction.transaction_status)}.main` }}
              >
                {TRANSACTION_STATUSES[transaction.transaction_status]}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Transaction Timeline */}
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" gutterBottom fontWeight="medium">
            Transaction Timeline
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="textSecondary">
                Created at:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {formatDate(transaction.created_at)}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="textSecondary">
                Last Updated:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {formatDate(transaction.updated_at)}
              </Typography>
            </Box>

            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="textSecondary">
                Processing Time:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {Math.ceil(
                  (new Date(transaction.updated_at).getTime() -
                    new Date(transaction.created_at).getTime()) /
                    (1000 * 60)
                )}{' '}
                minutes
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Additional Actions */}
      <Box mt={4} display="flex" gap={2}>
        <Box
          component="button"
          onClick={() => console.log('Refresh transaction')}
          sx={{
            px: 2,
            py: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            background: 'transparent',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Typography variant="body2">Refresh Status</Typography>
        </Box>

        <Box
          component="button"
          onClick={() => console.log('View raw data')}
          sx={{
            px: 2,
            py: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            background: 'transparent',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Typography variant="body2">View Raw Data</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default TransactionDetailPage;
